import BlogLayout from "../components/BlogLayout";
import { getPostBySlug } from "../config/blogPosts";

const FAQS = [
  {
    q: "Do I need a confirmed flight ticket to apply for a Japan visa from India?",
    a: "No. A dummy ticket or hold reservation with an active PNR is accepted across all VFS centers. You do not need to buy a non-refundable ticket before your visa is approved.",
  },
  {
    q: "How many years of ITR do I need for a Japan visa?",
    a: "It depends on your city. Delhi and Mumbai require 1 year. Bengaluru requires 2 years. Chennai requires 3 years. Always check the latest VFS checklist for your jurisdiction before submitting.",
  },
  {
    q: "Are free-cancellation hotel bookings accepted?",
    a: "Yes. You do not need to pay in full for accommodation before your visa is approved. A verifiable hotel booking confirmation under your name is sufficient at the application stage.",
  },
  {
    q: "Can I apply for a 5-year multiple entry Japan visa?",
    a: "You can request one, but it is issued at the embassy's discretion. Eligibility is based on prior Japan travel, travel to G7 countries, or demonstrated financial capacity. Japan may still issue a single entry visa after reviewing your file.",
  },
  {
    q: "What is the processing time for a Japan visa from India?",
    a: "Typically 5 to 14 working days depending on your city and the volume at the VFS center. Generate your flight reservation close to your submission date so the PNR remains active during review.",
  },
];

export default function BlogPostJapanVisaChecklist() {
  const post = getPostBySlug("japan-visa-checklist-india");

  return (
    <BlogLayout
      post={post}
      breadcrumbListTitle="Japan Visa Checklist India"
      faqs={FAQS}
      ctaHeading="Need a flight reservation for your Japan visa?"
      ctaButtonLabel="Get flight reservation for Japan visa"
    >
      <p>
        You have decided to visit Japan. Dates in mind, hotels shortlisted,
        itinerary half-planned. Then you open the visa checklist and the
        excitement hits a wall.
      </p>
      <p>
        Multiple documents. City-specific requirements. ITR for how many years?
        Bank statement from which date? Is a dummy ticket okay?
      </p>
      <p>
        This is a complete Japan visa document checklist for Indian applicants,
        including requirements for the 5-year multiple entry visa, organized so
        you can build your file and get it right the first time.
      </p>

      <h2>Who Needs a Visa to Visit Japan from India?</h2>
      <p>
        All Indian passport holders require a tourist visa to enter Japan. There
        is no visa on arrival option. Applications are handled through VFS
        centres under different Embassy and Consulate jurisdictions, and
        requirements can vary by city. Processing takes 5 to 14 working days.
      </p>

      <h2>The Core Checklist: Required at Every City</h2>
      <p>Build this foundation first before adding city-specific items.</p>
      <p>
        <strong>Passport:</strong> Current original passport with at least 6
        months validity beyond your travel dates, all old passports if any, and
        a photocopy of the first and last page.
      </p>
      <p>
        <strong>Visa application form:</strong> Fully filled, signed, and dated,
        with your passport photo attached. No corrections or overwriting.
      </p>
      <p>
        <strong>Photograph:</strong> White background, face clearly visible.
        Specifications vary by city — see the city section below. Carry both
        passport-size and 2x2 inch prints to be safe.
      </p>
      <p>
        <strong>Cover letter:</strong> More important than most applicants
        realize. Include your purpose of visit and exact travel dates, cities
        you plan to visit in Japan, who is funding the trip and names of
        accompanying persons, your employment or business status, and a clear
        commitment to return to India. Keep it factual, typed, and signed. One
        page is enough.
      </p>
      <p>
        <strong>Day-wise itinerary:</strong> Typed format with date, city,
        activity, and hotel name with contact for each day. Must match travel
        dates in your cover letter and flight reservation.
      </p>
      <p>
        <strong>Flight reservation:</strong> Required for all cities, covering
        both onward and return legs. A dummy ticket or hold reservation is
        accepted across all VFS centers. Ensure the PNR is active at the time
        of submission.
      </p>
      <p>
        <strong>Hotel bookings:</strong> Confirmation under the applicant's
        name. Free-cancellation bookings are acceptable. Solo applicants through
        Bengaluru should ensure the booking shows one guest only.
      </p>
      <p>
        <strong>Bank statements:</strong> Last 3 to 6 months depending on city,
        updated as close to submission date as possible. Must show consistent
        balance and sufficient funds.
      </p>
      <p>
        <strong>ITR acknowledgement:</strong> Number of years varies by city,
        covered in the table below.
      </p>
      <p>
        <strong>Employment proof:</strong> NOC or leave approval from employer,
        employment certificate on company letterhead, and latest 3 months
        payslips (mandatory for Bengaluru, strongly recommended elsewhere).
      </p>

      <h2>City-Wise Requirements</h2>
      <p>
        <strong>Delhi:</strong> Latest ITR or 6 months bank statement if ITR is
        unavailable. Flight schedule and itinerary with hotel contacts. Cover
        letter recommended for any profile that needs explanation.
      </p>
      <p>
        <strong>Mumbai:</strong> Cover letter is effectively mandatory. Detailed
        itinerary, hotel and flight booking, latest ITR plus 3 months bank
        statement, employer NOC. Travel history table for the last 2 years
        covering Australia, Canada, Europe, USA, and Japan. The most structured
        file among all four cities.
      </p>
      <p>
        <strong>Bengaluru:</strong> Original signed cover letter with travel
        dates, accompanying persons, and expense details. NOC and leave
        approval, 3 months salary bank statement updated to submission date,
        latest 2 years ITR, latest 3 months payslips. Occupational proof
        depending on profile: GST registration, KMC certificate, Bar Council
        certificate, or property documents. Group or family applicants should
        submit files together.
      </p>
      <p>
        <strong>Chennai:</strong> Typed schedule of stay, last 3 years ITR, 3
        months bank statement, employment certificate plus NOC, flight and hotel
        reservation. Sponsor or dependent documents if applying with spouse,
        child, or as a student.
      </p>

      <h2>Photograph Specifications by City</h2>
      <ul>
        <li>Delhi: Passport-size, white background</li>
        <li>Mumbai: 2x2 inch, white background</li>
        <li>Bengaluru: 45mm x 35mm, 80% face coverage, white background</li>
        <li>Chennai: Passport-size, white background</li>
      </ul>

      <h2>Sponsor Documents</h2>
      <p>
        If someone else is funding your trip, include a sponsor declaration or
        cover letter, sponsor's bank statement for the last 3 to 6 months,
        sponsor's ITR acknowledgement, sponsor's ID proof, and relationship
        proof such as a marriage certificate, birth certificate, or passport
        page with spouse name.
      </p>

      <h2>The 5-Year Multiple Entry Japan Visa</h2>
      <p>
        Japan offers a multiple entry tourist visa with up to 5 years validity
        and stays of up to 90 days per visit. This is not automatically granted.
        It is issued at the embassy's discretion after examining your profile
        against specific eligibility categories.
      </p>
      <p>You may be eligible if you have:</p>
      <ul>
        <li>A history of prior Japan visits</li>
        <li>
          Prior travel to Japan plus any G7 country (USA, UK, Canada, France,
          Germany, Italy)
        </li>
        <li>Demonstrated sufficient financial capacity</li>
        <li>
          A spouse or child relationship with an applicant who qualifies under
          financial capacity
        </li>
      </ul>
      <p>
        For financial capacity, Japan uses ITR as the primary proof. You can
        also include fixed deposit or balance certificates, investment
        statements, pension documents, rental income proof, or property deeds.
      </p>
      <p>When requesting a 5-year visa, add the following to your file:</p>
      <ul>
        <li>
          A specific mention in your cover letter requesting multiple entry and
          stating your eligibility basis
        </li>
        <li>ITR for the last 2 to 3 years showing consistent income</li>
        <li>
          Evidence of strong ties to India: property ownership, business
          registration, or family dependents
        </li>
        <li>Prior Japan visa copies or stamps if applicable</li>
        <li>Prior G7 country visa copies if using that as your eligibility basis</li>
      </ul>
      <p>
        Requesting a multiple entry visa does not guarantee one. Position it as
        a request, not an expectation, and ensure your core tourist file is
        strong regardless.
      </p>

      <h2>Quick Reference: Documents by City</h2>
      <ul>
        <li>
          <strong>Passport and copies:</strong> All cities
        </li>
        <li>
          <strong>Application form and photo:</strong> All cities
        </li>
        <li>
          <strong>Cover letter:</strong> Recommended in Delhi, mandatory in
          Mumbai and Bengaluru, required in Chennai
        </li>
        <li>
          <strong>Day-wise itinerary:</strong> All cities
        </li>
        <li>
          <strong>Flight reservation:</strong> All cities
        </li>
        <li>
          <strong>Hotel booking:</strong> All cities
        </li>
        <li>
          <strong>Bank statement:</strong> 6 months for Delhi, 3 months for
          Mumbai, Bengaluru, and Chennai
        </li>
        <li>
          <strong>ITR:</strong> 1 year for Delhi and Mumbai, 2 years for
          Bengaluru, 3 years for Chennai
        </li>
        <li>
          <strong>Payslips:</strong> Recommended for Delhi, required for Mumbai
          and Chennai, mandatory for Bengaluru
        </li>
        <li>
          <strong>NOC / leave letter:</strong> All cities
        </li>
        <li>
          <strong>Travel history table:</strong> Mumbai only
        </li>
      </ul>

      <h2>Common Mistakes That Delay or Reject Applications</h2>
      <p>
        <strong>Expired PNR on flight reservation.</strong> Dummy tickets hold
        for 24 to 72 hours. Generate yours close to your submission date, not
        weeks in advance.
      </p>
      <p>
        <strong>Outdated bank statement.</strong> A statement printed two months
        before submission does not reflect your current finances. Update it as
        late as possible.
      </p>
      <p>
        <strong>Itinerary dates not matching flight reservation.</strong> Any
        date mismatch between documents raises an immediate flag with the
        reviewing officer.
      </p>
      <p>
        <strong>Vague cover letter.</strong> "I wish to visit Japan for tourism"
        is not enough. Be specific about your plans, your finances, and your
        intent to return.
      </p>
      <p>
        <strong>Missing relationship proof for family applications.</strong> If
        travelling with a spouse or children, document the relationship clearly
        with the appropriate certificates.
      </p>

      <h2>The Bottom Line</h2>
      <p>
        Every document you submit should point to the same conclusion: you are a
        genuine tourist, you have the means to fund the trip, and you are coming
        back to India.
      </p>
      <p>
        Use this checklist as your foundation. Layer in city-specific
        requirements. For the 5-year visa, add the financial capacity evidence
        and state your eligibility basis clearly in your cover letter. A clean,
        complete file is the only thing standing between you and that boarding
        pass.
      </p>

      <p className="gd-article-note">
        Need a flight reservation for your Japan visa? Get a verified
        reservation delivered in under 15 minutes.
      </p>

      <h2>Frequently Asked Questions</h2>
      <div className="gd-article-faqs" data-testid="article-faqs">
        {FAQS.map((f, i) => (
          <div className="gd-article-faq" key={i} data-testid={`article-faq-${i}`}>
            <h3 className="gd-article-faq-q">{f.q}</h3>
            <p className="gd-article-faq-a">{f.a}</p>
          </div>
        ))}
      </div>
    </BlogLayout>
  );
}